/**
 * Builds the group configuration payload for the API by omitting optional fields
 * when they are empty. The backend expects optional fields to be absent (or valid),
 * not empty strings.
 */

const trim = (s: string | undefined): string => (s ?? '').trim()

function hasAddress(values: {
  line1?: string
  city?: string
  state?: string
  zipCode?: string
}): boolean {
  return (
    trim(values.line1).length > 0 &&
    trim(values.city).length > 0 &&
    trim(values.state).length === 2 &&
    /^\d{5}(-\d{4})?$/.test(trim(values.zipCode ?? ''))
  )
}

export interface GroupConfigurationFormValues {
  groupName: string
  groupDescription?: string
  timezone?: string
  headquarters?: {
    address?: {
      line1?: string
      line2?: string
      city?: string
      state?: string
      zipCode?: string
    }
    primaryPhone?: string
    email?: string
    website?: string
  }
  brandingGuidelines?: {
    logoUrl?: string
    primaryColor?: string
    secondaryColor?: string
  }
  corporateContacts?: Array<{
    name: string
    email: string
    phone: string
    title: string
  }>
}

export interface GroupConfigurationPayload {
  groupName: string
  groupDescription?: string
  timezone?: string
  headquarters?: {
    address?: {
      line1: string
      line2?: string
      city: string
      state: string
      zipCode: string
    }
    primaryPhone?: string
    email?: string
    website?: string
  }
  brandingGuidelines?: {
    logoUrl?: string
    primaryColor?: string
    secondaryColor?: string
  }
  corporateContacts?: Array<{
    name: string
    email: string
    phone: string
    title: string
  }>
}

/**
 * Converts form values to API payload: only includes optional fields when they
 * have real values. Empty strings and empty address are omitted.
 */
export function buildGroupConfigurationPayload(values: GroupConfigurationFormValues): GroupConfigurationPayload {
  const payload: GroupConfigurationPayload = {
    groupName: trim(values.groupName),
  }

  const desc = trim(values.groupDescription ?? '')
  if (desc.length > 0) {
    payload.groupDescription = desc
  }

  if (values.timezone) {
    payload.timezone = values.timezone
  }

  const hq = values.headquarters
  if (hq) {
    const primaryPhone = trim(hq.primaryPhone ?? '')
    const email = trim(hq.email ?? '')
    const website = trim(hq.website ?? '')
    const address = hq.address
    const hasFullAddress = address ? hasAddress(address) : false

    if (primaryPhone || email || website || hasFullAddress) {
      payload.headquarters = {}
      if (primaryPhone) payload.headquarters.primaryPhone = primaryPhone
      if (email) payload.headquarters.email = email
      if (website) payload.headquarters.website = website
      if (hasFullAddress && address) {
        payload.headquarters.address = {
          line1: trim(address.line1!),
          city: trim(address.city!),
          state: trim(address.state!),
          zipCode: trim(address.zipCode!),
        }
        const line2 = trim(address.line2 ?? '')
        if (line2) payload.headquarters.address.line2 = line2
      }
    }
  }

  const branding = values.brandingGuidelines
  if (branding) {
    const logoUrl = trim(branding.logoUrl ?? '')
    const primaryColor = trim(branding.primaryColor ?? '')
    const secondaryColor = trim(branding.secondaryColor ?? '')
    if (logoUrl || primaryColor || secondaryColor) {
      payload.brandingGuidelines = {}
      if (logoUrl) payload.brandingGuidelines.logoUrl = logoUrl
      if (primaryColor) payload.brandingGuidelines.primaryColor = primaryColor
      if (secondaryColor) payload.brandingGuidelines.secondaryColor = secondaryColor
    }
  }

  if (values.corporateContacts && values.corporateContacts.length > 0) {
    payload.corporateContacts = values.corporateContacts
  }

  return payload
}
